import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, HelpCircle, Heart, Mail, Book } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { T } from "@/constants/testIds";

export default function CMSDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [inReview, setInReview] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/cms/articles?status=draft").then((r) => setDrafts(r.data || [])).catch(() => {});
    api.get("/cms/articles?status=in_review").then((r) => setInReview(r.data || [])).catch(() => {});
  }, []);

  return (
    <div>
      <div className="mb-8">
        <p className="eyebrow">Welcome, {user?.name}</p>
        <h1 className="heading-2 mt-2">Editor's Desk</h1>
        <p className="body-copy mt-3 text-ink-muted">Signed in as {ROLE_LABEL[user?.role]}.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Published articles" value={stats?.articles ?? "—"} />
        <StatCard icon={Book} label="Bible studies" value={stats?.bible_studies ?? "—"} />
        <StatCard icon={HelpCircle} label="Q&A entries" value={stats?.questions ?? "—"} />
        <StatCard icon={Heart} label="Kids' stories" value={stats?.kids_stories ?? "—"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
        <div className="paper-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl" style={{ fontWeight: 500 }}>Drafts</h2>
            <Link to="/cms/articles?status=draft" className="text-xs text-forest hover:underline">See all</Link>
          </div>
          <ul className="mt-4 space-y-2">
            {drafts.slice(0, 5).map((a) => (
              <li key={a.id}>
                <Link to={`/cms/articles/${a.id}`} className="text-sm text-ink hover:text-forest">{a.title}</Link>
                <span className="ml-2 text-xs text-ink-muted">· {a.author_name}</span>
              </li>
            ))}
            {drafts.length === 0 && <li className="text-sm text-ink-muted">No drafts.</li>}
          </ul>
        </div>
        <div className="paper-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl" style={{ fontWeight: 500 }}>Awaiting review</h2>
            <Link to="/cms/articles?status=in_review" className="text-xs text-forest hover:underline">See all</Link>
          </div>
          <ul className="mt-4 space-y-2">
            {inReview.slice(0, 5).map((a) => (
              <li key={a.id}>
                <Link to={`/cms/articles/${a.id}`} className="text-sm text-ink hover:text-forest">{a.title}</Link>
              </li>
            ))}
            {inReview.length === 0 && <li className="text-sm text-ink-muted">Nothing awaiting review.</li>}
          </ul>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-serif text-xl" style={{ fontWeight: 500 }}>Quick actions</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/cms/articles/new" className="btn-primary" data-testid={T.cmsNewArticleBtn}>New Article</Link>
          <Link to="/cms/articles" className="btn-ghost">All Articles</Link>
          <Link to="/cms/prayer-requests" className="btn-ghost">Prayer Requests</Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="paper-card p-5">
      <Icon className="h-5 w-5 text-forest" />
      <p className="font-serif text-3xl mt-3" style={{ fontWeight: 500 }}>{value}</p>
      <p className="eyebrow mt-1 text-[10px]">{label}</p>
    </div>
  );
}
